import { useInfiniteQuery } from "@tanstack/react-query";
import { Receipt } from "lucide-react";
import { useState } from "react";

import { useOrganization } from "@/auth/organization";
import { AttemptCard, STATES } from "@/components/billing/PaymentAttempts";
import { Button, EmptyState, ErrorState, Spinner } from "@/components/ui";
import { portal } from "@/lib/api";
import type { PaymentAttemptState } from "@/lib/types";

const PAGE = 20;
const ORDER: PaymentAttemptState[] = [
  "failed",
  "needs_customer_action",
  "unknown",
  "processing",
  "successful",
  "refunded",
  "refund_failed",
];

/**
 * Everything that happened when the workspace tried to pay, newest first: payments that worked, were declined,
 * needed the bank's approval or could not be confirmed, with or without an invoice (a declined upgrade never
 * becomes one).
 */
export function PaymentActivity() {
  const { orgId } = useOrganization();
  const [state, setState] = useState<PaymentAttemptState | "all">("all");
  const pages = useInfiniteQuery({
    queryKey: ["payment-activity", orgId, state],
    queryFn: ({ pageParam }) =>
      portal.paymentActivity(orgId, {
        state: state === "all" ? undefined : state,
        page_token: pageParam ?? undefined,
        page_size: PAGE,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.next_page_token,
  });
  const rows = pages.data?.pages.flatMap((p) => p.payment_attempts) ?? [];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <p className="min-w-0 flex-1 text-sm text-fg-subtle">
          Every time a payment was tried, including ones that were declined and never became an invoice.
        </p>
        <select
          aria-label="Show"
          value={state}
          onChange={(e) => setState(e.target.value as PaymentAttemptState | "all")}
          className="h-[33px] cursor-pointer rounded-full border border-border bg-surface px-3 text-[12.5px] font-semibold text-fg-muted hover:bg-muted"
        >
          <option value="all">All payments</option>
          {ORDER.map((s) => (
            <option key={s} value={s}>
              {STATES[s].label}
            </option>
          ))}
        </select>
      </div>

      {pages.isPending ? (
        <Spinner />
      ) : pages.isError ? (
        <ErrorState error={pages.error} />
      ) : rows.length === 0 ? (
        <div className="rounded-[18px] border border-border bg-surface">
          <EmptyState
            icon={<Receipt className="size-5" aria-hidden />}
            title={state === "all" ? "No payments tried yet" : "Nothing here"}
            description={
              state === "all"
                ? "When you pay for a plan or a domain, each try appears here, whether or not it went through."
                : "No payment is in this state."
            }
            action={
              state !== "all" && (
                <Button variant="secondary" size="sm" onClick={() => setState("all")}>
                  Clear filter
                </Button>
              )
            }
          />
        </div>
      ) : (
        <ol className="flex flex-col gap-3" aria-label="Payment activity">
          {rows.map((a) => (
            <AttemptCard
              key={a.id}
              attempt={a}
              title={
                <>
                  {a.description ?? "Payment"}
                  <span className="font-normal text-fg-subtle">
                    {a.invoice_number ? ` · Invoice ${a.invoice_number}` : " · No invoice (not paid)"}
                  </span>
                </>
              }
            />
          ))}
        </ol>
      )}
      {pages.hasNextPage && (
        <Button
          variant="secondary"
          size="sm"
          className="self-center"
          loading={pages.isFetchingNextPage}
          onClick={() => void pages.fetchNextPage()}
        >
          {pages.isFetchingNextPage ? "Loading…" : "Load more"}
        </Button>
      )}
    </div>
  );
}
